import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, MapPin, Calendar, Clock, Users, Car, DollarSign, User, Loader2 } from 'lucide-react';
import axios from 'axios';

import { API } from '../../utils/apiConfig';

export const AdminRideDetailModal = ({ ride, onClose }) => {
  const { t } = useTranslation();
  const [eta, setEta] = useState(null);
  const [etaLoading, setEtaLoading] = useState(false);

  useEffect(() => {
    if (!ride) return;
    loadETA();
  }, [ride]);

  const loadETA = async () => {
    setEtaLoading(true);
    try {
      const resp = await axios.get(`${API}/tracking/eta`, {
        params: {
          from_city: ride.start_city,
          to_city: ride.destination_city || ride.end_city,
        },
      });
      setEta(resp.data);
    } catch (e) {
      console.error('ETA load failed:', e);
    } finally {
      setEtaLoading(false);
    }
  };

  if (!ride) return null;

  const vehicle = ride.vehicle_info;
  const driver = ride.driver;
  const statusColor = {
    available: '#22c55e', active: '#22c55e',
    completed: '#6b7280', cancelled: '#ef4444',
    in_progress: '#f59e0b',
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="admin-ride-detail-modal">
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '580px',
          margin: '40px auto',
          background: 'var(--bg-card, #fff)',
          borderRadius: '12px',
          padding: '0',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          maxHeight: '85vh',
          overflowY: 'auto',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid var(--border-color, #e5e7eb)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <h2 style={{ margin: 0, fontSize: '1.15rem' }}>{t('admin.rides.details')}</h2>
          <button
            onClick={onClose}
            data-testid="close-ride-detail-modal"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Route */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <MapPin size={18} style={{ color: '#22c55e' }} />
              <strong style={{ fontSize: '1.05rem' }}>{ride.start_city}</strong>
              <span style={{ opacity: 0.4 }}>&rarr;</span>
              <strong style={{ fontSize: '1.05rem' }}>{ride.destination_city || ride.end_city}</strong>
            </div>
            {/* ETA Badge */}
            {etaLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', opacity: 0.5 }}>
                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                <span>{t('admin.rides.loadingEta')}</span>
              </div>
            ) : eta ? (
              <div data-testid="ride-eta-badge" style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                background: 'var(--bg-hover, #f3f4f6)', padding: '6px 12px',
                borderRadius: '20px', fontSize: '0.85rem',
              }}>
                <Clock size={14} />
                <span style={{ fontWeight: 600 }}>{eta.eta_text}</span>
                <span style={{ opacity: 0.4 }}>|</span>
                <span>{eta.distance_km} km</span>
              </div>
            ) : null}
          </div>

          {/* Date & Time */}
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Calendar size={16} style={{ opacity: 0.5 }} />
              <span>{ride.departure_date || '-'}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Clock size={16} style={{ opacity: 0.5 }} />
              <span>{ride.departure_time || '-'}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Users size={16} style={{ opacity: 0.5 }} />
              <span>{ride.seats_available ?? '-'}/{ride.seats_total ?? '-'} {t('admin.rides.seats')}</span>
            </div>
          </div>

          {/* Status */}
          <div>
            <span style={{
              display: 'inline-block',
              padding: '4px 12px',
              borderRadius: '20px',
              fontSize: '0.8rem',
              fontWeight: 600,
              color: '#fff',
              background: statusColor[ride.status] || '#6b7280',
            }}>
              {ride.status || 'unknown'}
            </span>
          </div>

          {/* Driver Info */}
          {driver && (
            <div style={{
              background: 'var(--bg-hover, #f9fafb)',
              borderRadius: '10px',
              padding: '14px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <User size={16} />
                <strong>{t('admin.rides.driver')}</strong>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.9rem' }}>
                <span>{driver.name || '-'}</span>
                <span style={{ opacity: 0.6 }}>{driver.email || '-'}</span>
                {driver.phone && <span style={{ opacity: 0.6 }}>{driver.phone}</span>}
              </div>
            </div>
          )}

          {/* Vehicle Info */}
          {vehicle && (
            <div style={{
              background: 'var(--bg-hover, #f9fafb)',
              borderRadius: '10px',
              padding: '14px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Car size={16} />
                <strong>{t('admin.rides.vehicle')}</strong>
              </div>
              <div style={{ fontSize: '0.9rem' }}>
                <span style={{ fontWeight: 500 }}>{vehicle.make} {vehicle.model}</span>
                {vehicle.color && <span style={{ opacity: 0.6 }}> &middot; {vehicle.color}</span>}
                {vehicle.license_plate && (
                  <div style={{ marginTop: '4px', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {vehicle.license_plate}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Pricing */}
          <div style={{
            background: 'var(--bg-hover, #f9fafb)',
            borderRadius: '10px',
            padding: '14px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <DollarSign size={16} />
              <strong>{t('admin.rides.pricing')}</strong>
            </div>
            <div style={{ display: 'flex', gap: '20px', fontSize: '0.9rem' }}>
              <div>
                <span style={{ opacity: 0.6 }}>{t('admin.rides.pricePerSeat')}: </span>
                <strong>{ride.price_per_seat || ride.calculated_price || 0}&#8364;</strong>
              </div>
              {ride.calculated_price && (
                <div>
                  <span style={{ opacity: 0.6 }}>{t('admin.rides.totalPrice')}: </span>
                  <strong>{ride.calculated_price}&#8364;</strong>
                </div>
              )}
            </div>
          </div>

          {/* Ride ID */}
          <div style={{ fontSize: '0.75rem', opacity: 0.4, fontFamily: 'monospace' }}>
            ID: {ride.ride_id || ride.id}
          </div>
        </div>
      </div>
    </div>
  );
};
